<script>
    import camelCase from 'camelcase';
    import { generateEChartData, generateChartData } from '@/utils/chart';
    import { calculateChangePercentage } from '@/utils/number';

    import PageHeader from '@/components/common/PageHeader.vue';
    import TimeRangeCalendar from '@/components/common/TimeRangeCalendar.vue';
    import MultiEChartCard from '@/components/common/MultiEChartCard.vue';
    import WidgetStatChart from './parts/WidgetStatChart.vue';
    import WidgetCommission from './parts/WidgetCommission.vue';
    import WidgetNewCampaigns from './parts/WidgetNewCampaigns.vue';
    import WidgetRecentTransactions from './parts/WidgetRecentTransactions.vue';
    import WidgetBestSpaces from './parts/WidgetBestSpaces.vue';
    import WidgetBestCampaigns from './parts/WidgetBestCampaigns.vue';
    import WidgetBestProducts from './parts/WidgetBestProducts.vue';
    import TipboxCommission from './parts/TipboxCommission.vue';

    export default {
        name: 'DashboardMixin',
        components: {
            PageHeader,
            WidgetStatChart,
            TimeRangeCalendar,
            MultiEChartCard,
            WidgetCommission,
            TipboxCommission,
            WidgetNewCampaigns,
            WidgetRecentTransactions,
            WidgetBestSpaces,
            WidgetBestCampaigns,
            WidgetBestProducts,
        },
        provide() {
            return { parentComponent: this };
        },
        data() {
            return {
                timeRange: null,
                currentChart: 'impressions',

                requestImpressionsStats: this.$req('userStats/ImpressionsStats'),
                requestClicksStats: this.$req('userStats/ClicksStats'),
                requestOrdersStats: this.$req('userStats/OrdersStats'),
                requestCommissionStats: this.$req('userStats/CommissionStats'),
                requestAmountStats: this.$req('userStats/AmountStats'),
                requestSalesStats: this.$req('userStats/SalesStats'),
                requestPublisherQuantityStats: this.$req('userStats/PublisherQuantityStats'),
                requestSpacesQuantityStats: this.$req('userStats/SpacesQuantityStats'),

                requestOrdersChart: this.$req('userStats/OrdersChart'),
                requestAdminCommissionChart: this.$req('userStats/AdminCommissionChart'),
                requestAdvertiserCommissionChart: this.$req('userStats/AdvertiserCommissionChart'),
                requestTrafficChart: this.$req('userStats/TrafficChart'),
                requestSalesChart: this.$req('userStats/SalesChart'),

                requestCampaigns: this.$req('buyboxes/AvailableCampaigns'),
                requestUserTransactions: this.$req('transactions/userTransactions'),
                requestSpaceReport: this.$req('userStats/spaceReport'),
                requestTopProductsQuantity: this.$req('userStats/topProductsQuantity'),
                requestCampaignReport: this.$req('userStats/campaignReport'),
            };
        },
        computed: {
            isRoleAdmin() {
                return this.$store.getters['auth/isRoleAdmin'];
            },
            requestParams() {
                return { ...(this.timeRange || {}) };
            },
            apiParams() {
                return { requestParams: this.requestParams };
            },
            apiUserType() {
                return this.$store.getters['auth/apiUserType'];
            },
            impressions() {
                return this.requestImpressionsStats.getData() || {};
            },
            clicks() {
                return this.requestClicksStats.getData() || {};
            },
            orders() {
                return this.requestOrdersStats.getData() || {};
            },
            commission() {
                return this.requestCommissionStats.getData() || {};
            },
            amount() {
                return this.requestAmountStats.getData() || {};
            },
            sales() {
                return this.requestSalesStats.getData() || {};
            },
            publisherQuantity() {
                return this.requestPublisherQuantityStats.getData() || {};
            },
            spacesQuantity() {
                return this.requestSpacesQuantityStats.getData() || {};
            },
            ordersChart() {
                return this.requestOrdersChart.getData() || {};
            },
            adminCommissionChart() {
                return this.requestAdminCommissionChart.getData() || {};
            },
            advertiserCommissionChart() {
                return this.requestAdvertiserCommissionChart.getData() || {};
            },
            trafficChart() {
                return this.requestTrafficChart.getData() || [];
            },
            salesChart() {
                return this.requestSalesChart.getData() || [];
            },
            isChartOverviewLoading() {
                const key = camelCase(this.currentChart, { pascalCase: true });
                return this[`request${key}Stats`]?.getLoading() || false;
            },
            chartOverviewOptions() {
                return {
                    markers: { size: 0 },
                    colors: ['#5369f8'],
                    tooltip: {
                        y: {
                            formatter: numericValue => numericValue,
                        },
                    },
                    yaxis: {
                        title: { text: '' },
                        labels: {
                            formatter: numericValue => numericValue,
                        },
                    },
                };
            },
        },
        watch: {
            apiParams() {
                this.fetchData();
            },
        },
        methods: {
            getChartItem(data, name = '', color = '#5369f8') {
                return {
                    series: [{ data, name }],
                    name,
                    color,
                };
            },
            getChartData(chartArray, valueKey, name, color, dataType) {
                const rawPairs = (chartArray || []).map(row => [row.date, row[valueKey]]);
                const timePairs = generateEChartData(this.timeRange, rawPairs);
                const chartOptions = {
                    name,
                    color,
                    series: [
                        {
                            name,
                            type: 'line',
                            data: timePairs,
                            color,
                        },
                    ],
                    legend: { show: false },
                };
                if (dataType === 'currency') chartOptions.tooltip = { valueFormatter: val => this.$format.currency(val) };
                return chartOptions;
            },
            getChartDataLegacy(chartArray, valueKey) {
                const pairs = (chartArray || []).map(row => [row.date, row[valueKey]]);
                return generateChartData(this.timeRange, pairs);
            },
            getWidgetChartData(key) {
                return this.chartOverview?.[key]?.series?.[0]?.data?.map(item => item[1]) || [];
            },
            percentDiff(newValue, oldValue) {
                return calculateChangePercentage(newValue, oldValue);
            },
            setTimeRange(selectedRange) {
                this.timeRange = selectedRange;
            },
            setCurrentChart(metricKey) {
                this.currentChart = metricKey;
            },
            fetchImpressionsStats(params = {}) {
                this.requestImpressionsStats.get({ ...this.apiParams, ...params });
            },
            fetchClicksStats(params = {}) {
                this.requestClicksStats.get({ ...this.apiParams, ...params });
            },
            fetchOrdersStats(params = {}) {
                this.requestOrdersStats.get({ ...this.apiParams, ...params });
            },
            fetchCommissionStats(params = {}) {
                this.requestCommissionStats.get({ ...this.apiParams, ...params });
            },
            fetchAmountStats(params = {}) {
                this.requestAmountStats.get({ ...this.apiParams, ...params });
            },
            fetchSalesStats(params = {}) {
                this.requestSalesStats.get({ ...this.apiParams, ...params });
            },
            fetchPublisherQuantityStats(params = {}) {
                this.requestPublisherQuantityStats.get({ ...this.apiParams, ...params });
            },
            fetchSpacesQuantityStats(params = {}) {
                this.requestSpacesQuantityStats.get({ ...this.apiParams, ...params });
            },
            fetchOrdersChart(params = {}) {
                this.requestOrdersChart.get({ ...this.apiParams, ...params });
            },
            fetchAdminCommissionChart(params = {}) {
                this.requestAdminCommissionChart.get({ ...this.apiParams, ...params });
            },
            fetchAdvertiserCommissionChart(params = {}) {
                this.requestAdvertiserCommissionChart.get({ ...this.apiParams, ...params });
            },
            fetchTrafficChart(params = {}) {
                this.requestTrafficChart.get({ ...this.apiParams, ...params });
            },
            fetchSalesChart(params = {}) {
                this.requestSalesChart.get({ ...this.apiParams, ...params });
            },
            fetchData() {},
        },
    };
</script>
